import React, { FC, FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isUndefined } from '../../predicates/type';
import { floatFormat } from '../CustomForm/utils/format/number';
import { floatNormalizer, intNormalizer, pinCodeNormalizer } from '../CustomForm/utils/normalize/number';
import { Textbox } from '../Textbox';
import { DEFAULT_NUMBER_TEXTBOX_INPUT_TEST_ID } from './const';
import { NumberTextboxProps } from './types';
import { formatValueOnBlur, normalizeValueOnBlur } from './utils';

export const NumberTextbox: FC<React.PropsWithChildren<NumberTextboxProps>> = (props) => {
	const {
		allowComma,
		onChange,
		onBlur,
		onFocus,
		min,
		max,
		allowNegativeValue,
		value: valueFromProps,
		decimalScale,
		digitsAfterDot,
		pinCodeMode,
		...textBoxProps
	} = props;
	const hasFocusRef = useRef(false);
	const [value, setValue] = useState(valueFromProps);
	const syncValue = useCallback(
		(newValue: string | number) => {
			setValue(newValue);
			onChange && onChange(newValue);
		},
		[onChange],
	);
	const prevValueRef = useRef(value);
	const getNormalizedValue = useCallback(
		(value: string) => {
			const prevValue = prevValueRef.current;

			if (pinCodeMode) {
				return pinCodeNormalizer()(value, prevValue);
			}

			if (allowComma) {
				return floatNormalizer({
					min,
					max,
					allowNegativeValue,
					digitsAfterDot,
				})(value, prevValue);
			}
			return intNormalizer({
				min,
				max,
				allowNegativeValue,
			})(value, prevValue);
		},
		[pinCodeMode, allowComma, min, max, allowNegativeValue, digitsAfterDot],
	);
	const handleTextboxChange = useCallback(
		(value: string) => {
			const nextValue = getNormalizedValue(value);

			if (isUndefined(nextValue)) {
				return;
			}

			syncValue(nextValue);
		},
		[getNormalizedValue, syncValue],
	);
	const handleTextboxBlur = useCallback(
		(e) => {
			hasFocusRef.current = false;
			onBlur && onBlur(e);

			if (pinCodeMode) {
				return;
			}

			const value = e.currentTarget.value;
			const nextValue = normalizeValueOnBlur(value, {
				allowComma,
				allowNegativeValue,
			});

			syncValue(nextValue);

			const newFormattedValue = formatValueOnBlur(nextValue, {
				decimalScale,
			});

			setValue(newFormattedValue);
		},
		[allowComma, allowNegativeValue, decimalScale, onBlur, pinCodeMode, syncValue],
	);
	const handleTextboxFocus = useCallback(
		(event: FocusEvent<HTMLInputElement>) => {
			hasFocusRef.current = true;
			onFocus && onFocus(event);
		},
		[onFocus],
	);
	const formattedValue = useMemo(() => {
		const formatOptions: Parameters<typeof floatFormat>[0] = {};

		if (!isUndefined(decimalScale) && !hasFocusRef.current) {
			formatOptions.decimalScale = decimalScale;
		}

		return floatFormat(formatOptions)(value);
	}, [decimalScale, value]);

	prevValueRef.current = value;

	useEffect(() => {
		if (hasFocusRef.current) {
			setValue(valueFromProps);

			return;
		}

		const formatOptions: Parameters<typeof floatFormat>[0] = {};

		if (!isUndefined(decimalScale)) {
			formatOptions.decimalScale = decimalScale;
		}

		const newInternalValue = floatFormat(formatOptions)(valueFromProps);

		setValue(newInternalValue);
	}, [decimalScale, valueFromProps]);

	useEffect(() => {
		if (!isUndefined(decimalScale) && !isUndefined(digitsAfterDot) && decimalScale !== digitsAfterDot) {
			throw new Error('@NumberTextbox, please sync "digitsAfterDot" and "decimalScale" values');
		}
	}, [decimalScale, digitsAfterDot]);

	return (
		<Textbox
			{...textBoxProps}
			onChange={handleTextboxChange}
			onFocus={handleTextboxFocus}
			onBlur={handleTextboxBlur}
			value={formattedValue}
			inputMode={allowComma ? 'decimal' : 'numeric'}
			dataTestIdField={DEFAULT_NUMBER_TEXTBOX_INPUT_TEST_ID}
		/>
	);
};

NumberTextbox.displayName = 'NumberTextbox';
